/**
 * Shared child-process `DeployHandle` plumbing for the loopback process targets
 * (`local`, `nix`). Both stand a workspace up as a host child process reached
 * over `ws://127.0.0.1:<port>`; the only differences are the command they spawn
 * and the config they accept. The handle lifecycle (ready → health via pid
 * liveness → buffered logs → SIGTERM stop) is identical, so it lives here once.
 */

import { type PortableSubprocess, portableSpawn } from "@skaile/workspaces/core";
import {
  type DeployContext,
  type DeployHandle,
  makeDeployHandle,
} from "@skaile/workspaces/plugin-registry";
import { LineBuffer, pumpLines } from "./stream-lines.js";

/** Minimum payload a process target persists to re-attach on restore. */
export interface ProcessPayload {
  pid: number;
  port: number;
}

/** A spawned child plus the buffer its stdout/stderr are pumped into. */
export interface SpawnedProcess {
  proc: PortableSubprocess;
  buffer: LineBuffer;
}

/** True when a pid is alive (signal 0 probes without delivering a signal). */
export function pidAlive(pid: number): boolean {
  // Reject pid <= 0 — kill(0/-1, …) targets process groups, never a single
  // process, so it must not count as "alive" (and stop() must not signal it).
  if (pid <= 0) return false;
  try {
    process.kill(pid, 0);
    return true;
  } catch {
    return false;
  }
}

/** Spawn `command args` and pump both stdout and stderr into one LineBuffer. */
export function spawnProcess(
  command: string,
  args: string[],
  opts: { cwd: string; env: Record<string, string | undefined> },
): SpawnedProcess {
  // This child is meant to outlive us: we read stdout only until readiness,
  // then exit and re-attach by pid later. Without this it would take the
  // dropped pipe as its cue to exit, and `restore()` would find no process.
  const env = { ...opts.env, SKAILE_SERVE_EXIT_ON_BROKEN_STDOUT: "0" };
  const proc = portableSpawn([command, ...args], { cwd: opts.cwd, env });
  const buffer = new LineBuffer();
  void pumpLines(proc.stdout, buffer);
  void pumpLines(proc.stderr, buffer);
  return { proc, buffer };
}

/**
 * Build the `DeployHandle` for a process target via the shared
 * {@link makeDeployHandle} factory. `proc` is `null` for a restored handle
 * (re-attached by pid — no live stream, so `logs()` yields nothing and the
 * handle starts already `ready`). `defaultReadyTimeoutMs` is the timeout
 * `ready` gets when `waitReady()` is called without one.
 */
export function buildProcessHandle<P extends ProcessPayload>(
  payload: P,
  ctx: DeployContext,
  ready: (timeoutMs: number, signal: AbortSignal) => Promise<void>,
  proc: SpawnedProcess | null,
  defaultReadyTimeoutMs: number,
): DeployHandle<P> {
  return makeDeployHandle<P>({
    wsUrl: `ws://127.0.0.1:${payload.port}`,
    ctx,
    payload,
    initialState: proc ? "starting" : "ready",
    defaultReadyTimeoutMs,
    ready,
    async health() {
      const healthy = pidAlive(payload.pid);
      return healthy ? { healthy } : { healthy, detail: `pid ${payload.pid} not alive` };
    },
    async *logs(opts) {
      if (!proc) return; // a restored handle re-attaches by pid; it has no stream.
      const since = opts?.sinceMs ?? 0;
      for (const l of proc.buffer.since(since)) yield l;
      if (!opts?.follow) return;
      const queue: { ts: number; line: string }[] = [];
      let notify: (() => void) | null = null;
      const unsub = proc.buffer.subscribe((l) => {
        queue.push(l);
        notify?.();
      });
      try {
        while (!ctx.signal.aborted) {
          if (queue.length === 0) {
            await new Promise<void>((r) => {
              notify = r;
            });
            notify = null;
          }
          while (queue.length > 0) yield queue.shift() as { ts: number; line: string };
        }
      } finally {
        unsub();
      }
    },
    async stop() {
      if (pidAlive(payload.pid)) {
        try {
          process.kill(payload.pid, "SIGTERM");
        } catch {
          // Already gone between the check and the kill — idempotent.
        }
      }
    },
  });
}
